// apps/web/src/components/threat-actors/ThreatActorPulseStrip.tsx
//
// KAN-221 — "Actor pulse": a compact, corpus-wide KPI strip. Every value comes
// from the dedicated /kpis aggregate (ThreatActorKpis), NOT the capped list
// response, so the figures are registry-wide and coherent with active filters.
// Reference-rich is deliberately NOT shown here (it is a loaded-view figure and
// would be dishonest presented as corpus-wide — it lives in the context rail).

import { cn } from '@/lib/utils'
import type { ThreatActorKpis } from '@/lib/types'
import {
  attributionBarClass,
  attributionLabel,
  attributionMix,
  dominantShare,
} from './threat-actor-utils'

const LABEL = 'text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground'
const SUB = 'mt-1.5 text-[11.5px] text-muted-foreground'

function Cell({
  className,
  children,
  ...props
}: {
  className?: string
  children: React.ReactNode
} & React.HTMLAttributes<HTMLLIElement>) {
  return (
    <li className={cn('bg-card p-4', className)} {...props}>
      {children}
    </li>
  )
}

export interface ThreatActorPulseStripProps {
  kpis?: ThreatActorKpis
}

export function ThreatActorPulseStrip({ kpis }: ThreatActorPulseStripProps) {
  const mix = kpis ? attributionMix(kpis) : []
  const dominant = kpis?.dominantAttribution ?? null

  return (
    <ul
      data-testid="kpi-rail"
      aria-label="Actor registry pulse"
      className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-border bg-border lg:grid-cols-3"
    >
      <Cell data-testid="kpi-tile-dominant-attribution">
        <span className={LABEL}>Dominant class</span>
        <p className="mt-2 font-heading text-lg font-semibold leading-tight text-foreground">
          {dominant ? attributionLabel(dominant) : '—'}
        </p>
        <p className={SUB}>
          {kpis && dominant ? `${dominantShare(kpis)}% of tracked actors` : 'no dominant class'}
        </p>
      </Cell>

      <Cell className="col-span-2 lg:col-span-2">
        <span className={LABEL}>Attribution mix</span>
        <div className="mt-3 flex h-2 overflow-hidden rounded-full bg-muted" aria-hidden="true">
          {mix.map((seg) => (
            <span
              key={seg.key}
              className={attributionBarClass(seg.key)}
              style={{ width: `${seg.pct}%` }}
            />
          ))}
        </div>
        <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1.5">
          {mix.map((seg) => (
            <span key={seg.key} className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
              <span className={cn('h-2 w-2 rounded-sm', attributionBarClass(seg.key))} aria-hidden="true" />
              {seg.label}
              <span className="font-mono text-muted-foreground/70">{seg.count.toLocaleString('en-GB')}</span>
            </span>
          ))}
        </div>
      </Cell>
    </ul>
  )
}
